import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import FormSectionAccordion from "../../../../../components/FormSectionAccordion"
import theme from "../../../../../theme/theme"
import { ReactComponent as CheckIcon } from "../../../../../assets/icons/check-icon.svg"
import PropTypes from "prop-types"
import { renderGrid } from "../../../../../components/FieldRenderer"
import { mapDadosGarantias, mapPayloadRatingEmissao, mapRatingEmissao, ratingFields } from "./constants"
import { Box } from "@mui/material"
import ButtonWithIcon from "../../../../../components/Button/Button"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import EmissaoService from "../../../../../../services/emissao-service"
import DuplaConfirmacao from "../../../../../components/DuplaConfirmacao/DuplaConfirmacao"
import { dispatchSnackbar } from "../../../../../components/SnackBar/snackbar-bus"
import { getApiErrorMessage } from "src/v2/utils/api-error"

const RatingSection = (
    {
        caracteristicasState,
        garantiasState,
        state,
        setRatingState,
        setSectionField,
        setLoading,
        isValid,
        showError,
        actions,
        defaultOpen = false,
        syncToken = 0,
        autoCompleteController = {}
    }) => {

    const ratingService = useMemo(() => new EmissaoService(), [])

    const [cetipRating, setCetipRating] = useState(null)
    const ultimaConsultaRef = useRef(null)
    const [cadastrarRatingOpen, setCadastrarRatingOpen] = useState(false)
    const [ratingRegistered, setRatingRegistered] = useState(false)

    const [ratingStatus, setRatingStatus] = useState("idle")

    const consultaIdRef = useRef(0)
    const calculoIdRef = useRef(0)
    const cetipAnteriorRef = useRef(null)

    const analiseEditadaManualmenteRef = useRef(false)

    const dadosGarantiasSerializados = JSON.stringify(
        mapDadosGarantias(
            caracteristicasState,
            garantiasState
        )
    )

    useEffect(() => {
        const calculoId = ++calculoIdRef.current

        if (ratingStatus !== "new") return
        if (analiseEditadaManualmenteRef.current) return

        const timeoutId = setTimeout(async () => {
            if (analiseEditadaManualmenteRef.current) {
                return
            }

            try {
                const dadosGarantias = JSON.parse(
                    dadosGarantiasSerializados
                )

                const response =
                    await ratingService
                        .calcularAnaliseCreditoEmissao(
                            dadosGarantias
                        )

                const respostaDesatualizada =
                    calculoId !== calculoIdRef.current

                if (
                    respostaDesatualizada
                    || analiseEditadaManualmenteRef.current
                ) {
                    return
                }

                const analise =
                    response?.body?.AnaliseCreditoEmissao

                if (typeof analise === "string") {
                    setSectionField(
                        "rating",
                        "AnaliseCreditoEmissao",
                        analise
                    )
                }
            } catch (error) {
                if (calculoId === calculoIdRef.current) {
                    console.error(
                        "Não foi possível calcular a análise "
                        + "de crédito da emissão",
                        error
                    )
                }
            }
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [
        dadosGarantiasSerializados,
        ratingService,
        ratingStatus,
        setSectionField,
    ])

    const handleRatingFieldChange = (key) => (value) => {
        if (key === "AnaliseCreditoEmissao") {
            analiseEditadaManualmenteRef.current = true
            calculoIdRef.current += 1
        }

        setSectionField(
            "rating",
            key,
            value
        )
    }

    const loadRatingDevedor = useCallback(
        async (cpfCnpj, consultaId) => {
            if ((cpfCnpj ?? "").length < 11) {
                return null
            }

            try {
                const response =
                    await ratingService.getUltimoRatingDevedor(
                        cpfCnpj,
                        "C2P",
                    )

                if (consultaId !== consultaIdRef.current) {
                    return null
                }

                const { body: { data } } = response

                if (data !== null) {
                    return {
                        ...mapRatingEmissao(data),
                        ID: null,
                        Observacao: "",
                    }
                }

                dispatchSnackbar(
                    "Rating devedor não cadastrado.",
                    "warning"
                )

                return null
            } catch (error) {
                if (consultaId === consultaIdRef.current) {
                    console.error(
                        "Erro ao consultar rating devedor",
                        error
                    )
                }

                return null
            }
        },
        [ratingService]
    )

    const loadRatingEmissao = useCallback(
        async (cetip, cpfCnpj, consultaId) => {
            if ((cetip ?? "").length < 4) return

            setRatingStatus("loading")
            setLoading(true)

            try {
                const response =
                    await ratingService
                        .getUltimoRatingEmissaoCetip(cetip)

                if (consultaId !== consultaIdRef.current) {
                    return
                }

                const { body: { data } } = response

                if (data != null) {
                    setRatingState(mapRatingEmissao(data))
                    setRatingRegistered(true)
                    setRatingStatus("existing")
                    return
                }

                setRatingRegistered(false)
                setRatingState({})

                const ratingDevedor = await loadRatingDevedor(
                    cpfCnpj,
                    consultaId
                )

                if (consultaId !== consultaIdRef.current) {
                    return
                }

                if (ratingDevedor !== null) {
                    setRatingState(ratingDevedor)
                }

                setRatingStatus("new")
            } catch (error) {
                if (consultaId !== consultaIdRef.current) {
                    return
                }

                setRatingState({})
                setRatingRegistered(false)
                setRatingStatus("error")

                dispatchSnackbar(
                    getApiErrorMessage(
                        error,
                        "Não foi possível carregar o rating da emissão."
                    ),
                    "error"
                )

                console.error(
                    "Não foi possível carregar os dados "
                    + "de rating emissão",
                    error
                )
            } finally {
                if (consultaId === consultaIdRef.current) {
                    setLoading(false)
                }
            }
        },
        [
            loadRatingDevedor,
            ratingService,
            setLoading,
            setRatingState,
        ]
    )

    useEffect(() => {
        const cetip = caracteristicasState?.cetip
        const devedor = caracteristicasState?.devedor

        if (cetipAnteriorRef.current !== cetip) {
            cetipAnteriorRef.current = cetip
            analiseEditadaManualmenteRef.current = false
        }

        if ((cetip ?? "").length < 4) {
            ultimaConsultaRef.current = null
            consultaIdRef.current += 1

            setCetipRating(null)
            setRatingRegistered(false)
            setRatingStatus("idle")
            setRatingState({})

            return
        }

        const chaveConsulta = `${cetip}|${devedor ?? ""}`

        if (ultimaConsultaRef.current === chaveConsulta) {
            return
        }

        ultimaConsultaRef.current = chaveConsulta

        const consultaId = ++consultaIdRef.current

        setCetipRating(cetip)

        loadRatingEmissao(
            cetip,
            devedor,
            consultaId
        )
    }, [caracteristicasState?.cetip, caracteristicasState?.devedor, loadRatingEmissao, setRatingState])

    const createRatingEmissao = () => {
        const body = mapPayloadRatingEmissao(
            cetipRating,
            state
        )

        setLoading(true)
        ratingService.ratingEmissao("post", body)
            .then((response) => {
                const mapped = mapRatingEmissao(response.body)

                setRatingState(mapped)
                setRatingRegistered(true)
                setRatingStatus("existing")
                dispatchSnackbar("Rating cadastrado com sucesso!", "success")
            })
            .catch((error) => {
                dispatchSnackbar(
                    getApiErrorMessage(error, "Não foi possível cadastrar o rating."),
                    "error"
                )
                console.error(error)
            })
            .finally(() => {
                setLoading(false)
                setCadastrarRatingOpen(false)
            })
    }

    const updateRatingEmissao = () => {
        const { ID } = state

        if (!ID) {
            dispatchSnackbar("Rating de emissão sem ID para atualização.", "error")
            return
        }

        const payload = mapPayloadRatingEmissao(
            cetipRating,
            state
        )

        setLoading(true)
        ratingService.ratingEmissao("patch", ID, payload)
            .then((response) => {
                const mapped = mapRatingEmissao(response.body)

                setRatingState(mapped)
                setRatingRegistered(true)
                setRatingStatus("existing")
                dispatchSnackbar("Rating atualizado com sucesso!", "success")
            })
            .catch((error) => {
                dispatchSnackbar(
                    getApiErrorMessage(error, "Não foi possível atualizar o rating."),
                    "error"
                )
                console.error(error)
            })
            .finally(() => {
                setCadastrarRatingOpen(false)
                setLoading(false)
            })
    }

    return (
        <>
            <FormSectionAccordion
                title={"Rating Emissão"}
                icon={<CheckIcon style={{ color: theme.palette.icon.main, width: 20, height: 20 }} />}
                isValid={isValid}
                showError={showError}
                actions={actions}
                defaultOpen={defaultOpen}
                syncToken={syncToken}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "right",
                        mt: 3,
                        mb: 3,
                    }}
                >
                    <ButtonWithIcon size="md" icon={<AddCircleOutlineIcon />}
                        disabled={ratingStatus === "loading" || ratingStatus === "error"}
                        onClick={() => setCadastrarRatingOpen(true)}>
                        {ratingRegistered ? "ATUALIZAR RATING" : "CADASTRAR RATING"}
                    </ButtonWithIcon>
                </Box>
                {renderGrid(
                    ratingFields,
                    state,
                    handleRatingFieldChange,
                    undefined,
                    undefined,
                    handleRatingFieldChange,
                    autoCompleteController
                )}
            </FormSectionAccordion>

            <DuplaConfirmacao
                open={cadastrarRatingOpen}
                cancel={() => setCadastrarRatingOpen(false)}
                confirm={ratingRegistered ? updateRatingEmissao : createRatingEmissao}
                title={`${ratingRegistered ? "Atualizar" : "Cadastrar"} Rating`}
                description={`O rating da emissão ${cetipRating} será ${ratingRegistered ? "atualizado" : "cadastrado"}. Deseja continuar?`}
            />

        </>
    )
}

RatingSection.propTypes = {
    caracteristicasState: PropTypes.object,
    garantiasState: PropTypes.object,
    state: PropTypes.object.isRequired,
    setRatingState: PropTypes.func.isRequired,
    setSectionField: PropTypes.func.isRequired,
    setLoading: PropTypes.func.isRequired,
    isValid: PropTypes.bool,
    showError: PropTypes.bool,
    actions: PropTypes.node,
    defaultOpen: PropTypes.bool,
    syncToken: PropTypes.number,
    autoCompleteController: PropTypes.object,
}

export default RatingSection